import _ from "lodash";
import { createSelector } from "reselect";

import { ApiResponseType } from "@superblocksteam/shared";

import {
  selectAllV2ApiNames,
  selectAllV2Apis,
} from "../../store/slices/apisV2/selectors";
import { getV2ApiId } from "../../store/slices/apisV2/utils/getApiIdAndName";
import type { AppState } from "../../store/types";
import { isOnEmbedRoute } from "../../utils/embed/messages";
import type { ApiInfo } from "../constants/ApiConstants";
import { KeyBindings } from "../constants/EditorPreferencesConstants";
import type { DeveloperPreferences } from "../constants/EditorPreferencesConstants";
import type { EmbedPropertyAndMetaMap } from "../constants/EmbeddingConstants";
import { PAGE_WIDGET_ID } from "../constants/WidgetConstants";
import type { WidgetMap } from "../widgets";
import { getMainContainer, getWidgets } from "./entitiesSelector";
import { createMarkedSelector } from "./markedSelector";

export const getWidgetsMeta = (state: AppState) => state.legacy.entities.meta;

// embed prop
export const getEmbedProperties = createMarkedSelector("getEmbedProperties")(
  getMainContainer,
  (main) => {
    return main?.embedding?.propertyMap || {};
  },
);

export const getAllEmbedPropNames = createSelector(
  [getEmbedProperties],
  (propertyMap) => {
    return Object.values(propertyMap).map((embedProp) => embedProp.name);
  },
);

export const getEmbedPropsAndMeta = createMarkedSelector(
  "getEmbedPropertyAndMeta",
)(
  [
    getEmbedProperties,
    (state: AppState) => state.legacy.entities.embedPropsMeta,
  ],
  (embedPropMap, metaMap) => {
    const allEmbedPropIds = new Set(
      Object.keys(embedPropMap).concat(Object.keys(metaMap)),
    );
    const embedPropAndMeta = [...allEmbedPropIds].reduce((acc, id) => {
      const embedProp = embedPropMap[id] ?? {};
      // if not actually in embedding mode, use the defaultValue as the metaMap.value
      const isEmbedded = isOnEmbedRoute();
      const embedPropMeta = isEmbedded
        ? metaMap[id]
        : { value: embedProp.defaultValue };
      return {
        ...acc,
        [id]: {
          embedProp,
          embedPropMeta,
        },
      };
    }, {} as EmbedPropertyAndMetaMap);

    return embedPropAndMeta;
  },
);

// embed events

export const getPageDSLVersion = (state: AppState): number | undefined =>
  state.legacy.entities.canvasWidgets[PAGE_WIDGET_ID]?.version as
    | number
    | undefined;

// TODO there are a few v1 usages that require v2 versions

export const getV2ApiAppInfo = createMarkedSelector("getV2ApiAppInfo")(
  getMainContainer,
  selectAllV2Apis,
  (mainContainer, apis) => {
    const apiMap = mainContainer?.apis?.apiMap ?? {};
    return Object.values(apis).reduce(
      (acc, api) => {
        const apiId = getV2ApiId(api);
        const apiInfo: ApiInfo | undefined = apiMap[apiId];
        acc[apiId] = apiInfo ?? { id: apiId };
        return acc;
      },
      {} as Record<string, ApiInfo>,
    );
  },
);

export const getV2ApiAppInfoById = createMarkedSelector("getV2ApiAppInfoById")(
  getV2ApiAppInfo,
  (_: AppState, id: ApiInfo["id"]) => id,
  (appInfo, id: ApiInfo["id"]) => {
    return (
      appInfo[id] ?? {
        id: id,
      }
    );
  },
);

export const selectAllV2NonStreamingApiNames = createSelector(
  selectAllV2Apis,
  getV2ApiAppInfo,
  (apis, appInfoById) => {
    return Object.keys(apis)
      .filter(
        (apiId) => appInfoById[apiId].responseType !== ApiResponseType.STREAM,
      )
      .map((apiId) => apis[apiId].apiPb.metadata.name);
  },
);

export const getPageCachedData = (state: AppState) => {
  const main = state.legacy.entities.canvasWidgets[PAGE_WIDGET_ID];
  return main?.cachedData;
};

export const getExistingWidgetNames = createMarkedSelector(
  "getExistingWidgetNames",
)(getWidgets, (widgets: WidgetMap) => {
  return Object.values(widgets)
    .map((widget) => widget.widgetName)
    .sort((a, b) => a.localeCompare(b));
});

export const getWidgetTypeByName = createMarkedSelector("getWidgetsByName")(
  getWidgets,
  (widgets: WidgetMap) => {
    return Object.fromEntries(
      Object.values(widgets).map((widget) => [widget.widgetName, widget.type]),
    );
  },
);

/**
 * returns a objects of existing page name in data tree
 *
 * @param state
 */
export const getExistingPageNames = (state: AppState) =>
  state.legacy.entities.pageList.pages.map((page) => page.pageName);

export const getAllEntityNames = createSelector(
  getExistingWidgetNames,
  getExistingPageNames,
  selectAllV2ApiNames,
  (widgetNames, pageNames, apiV2Names) => {
    return [...widgetNames, ...pageNames, ...apiV2Names];
  },
);

const getSelectedWidgetsIds = (_state: AppState): string[] => [];

export const getWidgetsAreSelected = createMarkedSelector(
  "getWidgetsAreSelected",
)(getSelectedWidgetsIds, (ids) => ids.length > 0);

export const getSelectedWidgetsParentId = (_state: AppState) => undefined;

const DEFAULT_SHARED_PREFS: DeveloperPreferences["shared"] = {
  keyBindings: KeyBindings.Default,
};

export const getDeveloperPreferences = (_state: AppState) =>
  ({
    shared: DEFAULT_SHARED_PREFS,
    application: { triggerFrontendEventHandlersOnManualRun: false },
  }) satisfies DeveloperPreferences;

export const getSharedDeveloperPreferences = (_state: AppState) =>
  DEFAULT_SHARED_PREFS;
